/**
 * Editorial content for the public story entry (steps 1–2).
 * Administrative nesting (authoritative for the UI):
 *   Région de Kindia
 *     ├─ Préfecture de Kindia → Madina-Oula
 *     └─ Préfecture de Forécariah → Kounounkan, Allassoya & Moussaya
 */

export type LandscapeId = "kounounkan" | "allassoya-moussaya" | "madina-oula";
export type PrefectureId = "kindia" | "forecariah";

export type LandscapePlace = {
  readonly id: LandscapeId;
  readonly label: string;
  readonly shortLabel: string;
  /** Approximate WGS84 for the simplified locator map. */
  readonly lat: number;
  readonly lon: number;
  readonly prefectureId: PrefectureId;
  readonly kind: string;
  readonly ecology: string;
  readonly communities: string;
  readonly zones: readonly string[];
  readonly canonicalIds: readonly string[];
};

export type PrefectureNode = {
  readonly id: PrefectureId;
  readonly label: string;
  readonly localities: readonly LandscapePlace[];
};

export type RegionNode = {
  readonly id: "kindia-region";
  readonly label: string;
  readonly prefectures: readonly PrefectureNode[];
};

export const STUDY_LANDSCAPES: readonly LandscapePlace[] = [
  {
    id: "kounounkan",
    label: "Kounounkan",
    shortLabel: "Kounounkan",
    lat: 9.53,
    lon: -12.87,
    prefectureId: "forecariah",
    kind: "Paysage forestier",
    ecology:
      "Dernier grand massif forestier côtier de Guinée : forêts semi-sempervirentes, plateaux et ravins, savanes d’altitude. Point chaud de biodiversité végétale, souvent cité pour sa valeur de conservation.",
    communities:
      "Les communautés riveraines dépendent du bois-énergie, des produits forestiers non ligneux, de l’eau et des terres agricoles en bas de pente.",
    zones: ["ZIP Kounounkan"],
    canonicalIds: ["nbs-pay-kounounkan", "nbs-zon-zip-kounounkan"],
  },
  {
    id: "allassoya-moussaya",
    label: "Allassoya & Moussaya",
    shortLabel: "Allassoya–Moussaya",
    lat: 9.42,
    lon: -12.9,
    prefectureId: "forecariah",
    kind: "Réserves forestières",
    ecology:
      "Ensemble territorial lié au Parc National des Hauts Plateaux de Kounounkan : mosaïque forêt–savane–cultures, pressions agricoles et de prélèvement plus visibles en périphérie.",
    communities:
      "Allassoya et Moussaya ancrent l’étude dans le vécu local : usages des ressources, perception de la dégradation, accès à l’eau et aux sols.",
    zones: ["Réserve Allassoya", "Réserve Moussaya"],
    canonicalIds: [
      "nbs-pay-allassoya-moussaya",
      "nbs-res-allassoya",
      "nbs-res-moussaya",
      "nbs-zon-allassoya",
      "nbs-zon-moussaya",
    ],
  },
  {
    id: "madina-oula",
    label: "Madina-Oula",
    shortLabel: "Madina-Oula",
    lat: 9.88,
    lon: -12.45,
    prefectureId: "kindia",
    kind: "Paysage forestier",
    ecology:
      "Paysage de transition entre plaines et collines : cultures, jachères, formations boisées et pressions sur les sols et l’eau. Sert de contrepoint territorial à Kounounkan.",
    communities:
      "Les moyens d’existence y sont étroitement liés à l’agriculture, à l’eau et aux services de régulation (érosion, fertilité) que l’étude cherche à rendre visibles.",
    zones: ["Zone Madina-Oula"],
    canonicalIds: ["nbs-pay-madina-oula", "nbs-zon-madina-oula"],
  },
] as const;

const byId = (id: LandscapeId): LandscapePlace =>
  STUDY_LANDSCAPES.find((place) => place.id === id)!;

/** Nested reading order: Région → Préfectures → Localités. */
export const STUDY_TERRITORY_TREE: RegionNode = {
  id: "kindia-region",
  label: "Région de Kindia",
  prefectures: [
    {
      id: "kindia",
      label: "Préfecture de Kindia",
      localities: [byId("madina-oula")],
    },
    {
      id: "forecariah",
      label: "Préfecture de Forécariah",
      localities: [byId("kounounkan"), byId("allassoya-moussaya")],
    },
  ],
};

export function getPrefecture(place: LandscapePlace): PrefectureNode {
  return STUDY_TERRITORY_TREE.prefectures.find((p) => p.id === place.prefectureId)!;
}

export function getPlacePath(place: LandscapePlace): {
  readonly region: string;
  readonly prefecture: string;
  readonly locality: string;
} {
  return {
    region: STUDY_TERRITORY_TREE.label,
    prefecture: getPrefecture(place).label,
    locality: place.label,
  };
}

export type StudyWhyLandscape = {
  readonly id: "kounounkan-allassoya-moussaya" | "madina-oula";
  readonly title: string;
  readonly location: string;
  readonly specificity: string;
  readonly species: readonly string[];
  readonly communityRole: string;
  readonly threats: readonly string[];
};

export const STUDY_WHY = {
  title: "Pourquoi cette étude ?",
  question: "Pourquoi s’intéresser à ces paysages ?",
  lead: "L’étude porte sur deux paysages du biome des forêts guinéennes d’Afrique de l’Ouest, dans le cadre du projet d’adaptation climatique fondée sur la nature (NbS — forêts guinéennes) : la réserve forestière de Kounounkan (communes d’Allassoya et Moussaya) et le paysage forestier de Madina-Oula.",
  profiles: [
    {
      id: "kounounkan-allassoya-moussaya",
      title: "Réserve forestière de Kounounkan (Allassoya & Moussaya)",
      location: "Préfecture de Forécariah · communes rurales d’Allassoya et Moussaya",
      specificity:
        "Haute importance écologique au sein des forêts guinéennes : rempart pour la biodiversité et la régulation climatique locale.",
      species: [
        "Espèces endémiques et menacées",
        "Picatharte de Guinée (Picathartes gymnocephalus)",
      ],
      communityRole:
        "Soutient la subsistance des populations locales (bois, terres, eau, sécurité alimentaire) et agit comme rempart de régulation climatique.",
      threats: ["Pâturage extensif", "Agriculture extensive", "Récolte de bois"],
    },
    {
      id: "madina-oula",
      title: "Paysage forestier de Madina-Oula",
      location: "Préfecture de Kindia · zone frontalière avec la Sierra Leone",
      specificity:
        "Paysage forestier frontalier abritant une faune diversifiée, déjà fragilisé par la dégradation de ses écosystèmes.",
      species: [
        "Chimpanzés",
        "Hippopotames",
        "Faune diversifiée associée aux milieux forestiers et aquatiques",
      ],
      communityRole:
        "Essentiel pour la régulation climatique et la subsistance des communautés locales, dont la sécurité alimentaire dépend des cycles naturels.",
      threats: [
        "Dégradation des écosystèmes",
        "Pressions d’usage sur les ressources",
        "Effets du changement climatique (températures, précipitations)",
      ],
    },
  ] satisfies readonly StudyWhyLandscape[],
} as const;
